<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-4xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 flex flex-col items-start gap-4 w-full">
          <router-link :to="{ name: 'music_analytics' }" class="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors text-sm font-medium">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Retour aux statistiques
          </router-link>
          
          <div class="flex items-center gap-6" v-if="genre">
            <div class="w-20 h-20 rounded-2xl overflow-hidden bg-emerald-500/20 text-emerald-500 shadow-md ring-4 ring-emerald-500/20 flex items-center justify-center">
              <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
            </div>
            <div class="flex flex-col">
              <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-600 capitalize">{{ genre.name }}</h1>
              <p class="text-zinc-400 mt-1 mb-4">{{ tracks.length }} titres likés</p>

              <div class="flex items-center gap-3">
                <button 
                  v-if="!playlistId && !creatingPlaylist" 
                  @click="createPlaylist"
                  class="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-bold py-2 px-4 rounded-xl text-sm flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                  Créer une playlist auto-synchro
                </button>

                <button 
                  v-if="creatingPlaylist" 
                  disabled
                  class="bg-emerald-500/50 text-zinc-950 font-bold py-2 px-4 rounded-xl text-sm flex items-center gap-2 cursor-not-allowed"
                >
                  <div class="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
                  Création en cours...
                </button>

                <a 
                  v-if="playlistId" 
                  :href="`https://open.spotify.com/playlist/${playlistId}`" 
                  target="_blank"
                  class="bg-zinc-800 hover:bg-zinc-700 text-emerald-400 border border-emerald-500/30 font-bold py-2 px-4 rounded-xl text-sm flex items-center gap-2 transition-all shadow-md"
                >
                  Ouvrir dans Spotify
                </a>
                
                <span v-if="playlistId" class="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <span class="w-2 h-2 mr-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
                  Synchronisation active
                </span>

                <button 
                  @click="askAiForSuggestions"
                  :disabled="aiLoading"
                  class="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-xl text-sm flex items-center gap-2 transition-all shadow-lg shadow-blue-500/20 ml-auto disabled:opacity-50"
                >
                  <svg v-if="!aiLoading" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                  <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {{ aiLoading ? 'Génération...' : '✨ Suggestions IA' }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Decorative blob -->
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
        <p class="text-red-400">{{ error }}</p>
      </div>

      <div v-if="aiSuggestion" class="bg-blue-900/20 border border-blue-500/30 p-6 rounded-3xl relative overflow-hidden">
        <div class="absolute -right-10 -top-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div class="relative z-10">
          <h3 class="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>
            Suggestions de l'IA
          </h3>
          <div class="prose prose-invert prose-blue max-w-none prose-p:leading-relaxed" v-html="formattedAiSuggestion"></div>
        </div>
      </div>

      <div v-if="!loading && !error" class="bg-zinc-900/40 backdrop-blur-sm rounded-3xl border border-zinc-800/50 p-2">
        <div class="space-y-1">
          <div 
            v-for="track in tracks" 
            :key="track.spotify_id"
            class="flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-800/50 transition-all duration-300"
          >
            <!-- Icon -->
            <div class="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
            </div>
            
            <!-- Info -->
            <div class="flex-1 min-w-0">
              <h3 class="font-bold text-zinc-200 truncate">{{ track.track_name }}</h3>
              <p class="text-sm text-zinc-500">{{ track.artist_name }} • Ajouté le {{ formatDate(track.added_at) }}</p>
            </div>
          </div>
          
          <div v-if="tracks.length === 0" class="p-8 text-center text-zinc-500">
            Aucun titre trouvé.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from '../../composables/useToast'

const route = useRoute()
const { error: showError } = useToast()

const loading = ref(true)
const error = ref('')

const genre = ref<{name: string}|null>(null)
const tracks = ref<any[]>([])
const playlistId = ref<string|null>(null)
const creatingPlaylist = ref(false)

const aiSuggestion = ref<string|null>(null)
const aiLoading = ref(false)

const formattedAiSuggestion = computed(() => {
  if (!aiSuggestion.value) return ''
  // Basic markdown to HTML conversion for bold and lists
  let text = aiSuggestion.value
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  text = text.replace(/\n- (.*?)(?=\n|$)/g, '<li class="ml-4 list-disc">$1</li>')
  text = text.replace(/\n/g, '<br>')
  return text
})

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date)
}

const fetchGenreDetail = async () => {
  try {
    const id = route.params.id
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/music/analytics/genre/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Genre introuvable.')
        }
        throw new Error('Erreur réseau')
    }
    
    const data = await response.json()
    genre.value = data.genre
    tracks.value = data.tracks
    playlistId.value = data.playlistId || null
  } catch (e: any) {
    console.error(e)
    error.value = e.message || "Impossible de charger le genre."
    showError(error.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchGenreDetail()
})

const createPlaylist = async () => {
  if (creatingPlaylist.value) return
  creatingPlaylist.value = true

  try {
    const id = route.params.id
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/music/analytics/genre/${id}/playlist`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) {
        throw new Error('Erreur lors de la création de la playlist.')
    }
    
    const data = await response.json()
    playlistId.value = data.playlistId
  } catch (e: any) {
    console.error(e)
    showError(e.message || "Impossible de créer la playlist.")
  } finally {
    creatingPlaylist.value = false
  }
}

const askAiForSuggestions = async () => {
  if (aiLoading.value) return
  aiLoading.value = true
  aiSuggestion.value = null

  try {
    const token = localStorage.getItem('apiToken')
    
    // On extrait un échantillon des 20 premiers titres pour le contexte IA
    const sample = tracks.value.slice(0, 20).map(t => `${t.track_name} par ${t.artist_name}`)
    
    const response = await fetch(`/api/music/ai/suggest`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'genre',
        name: genre.value?.name,
        sample: sample
      })
    })
    
    if (!response.ok) {
        throw new Error('Erreur lors de la récupération des suggestions.')
    }
    
    const data = await response.json()
    aiSuggestion.value = data.suggestion
  } catch (e: any) {
    console.error(e)
    showError(e.message || "Impossible d'obtenir des suggestions.")
  } finally {
    aiLoading.value = false
  }
}
</script>
